import { Button, ButtonProps, DropdownMenu, Flex } from '@workwhile/ui'
import { StatusForCompanyEnum } from 'api/enums'
import { AddWorkerToListModal } from 'components/AddWorkerToListModal'
import { GiveBonusModal } from 'components/GiveBonusModal'
import { ScheduleShiftModal } from 'components/ScheduleShiftNavigator'
import { SendTipModal } from 'components/SendTipModal'
import { useIsTippingEnabled } from 'hooks/useIsTippingEnabled'
import {
  ChevronDownIcon,
  ClockIcon,
  GiftIcon,
  Heart,
  Mail,
  PlusIcon,
  XIcon,
} from 'lucide-react'
import {
  useTipHistory,
  useUpdateWorkerStatusForCompanyMutation,
} from 'queries/worker'
import { ComponentProps, useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from 'ui'
import { BlockModal } from './components/BlockModal'
import { BlockedAlert } from './components/BlockedAlert'

type MenuButtonStyleProps = {
  slim?: boolean
}

const menuButtonAttrs = ({
  slim,
}: MenuButtonStyleProps): {
  variant: NonNullable<ButtonProps['variant']>
  kind: NonNullable<ButtonProps['kind']>
  px: number
} => ({
  variant: 'secondary',
  kind: slim ? 'small' : 'medium',
  px: slim ? 1 : 3,
})

const withIconStyles = `
  display: flex;
  align-items: center;
  gap: 8px;
`

const Trigger = styled(Button).attrs<MenuButtonStyleProps>(menuButtonAttrs)`
  ${withIconStyles}
`

const ActionButton = styled(Button).attrs<MenuButtonStyleProps>(
  menuButtonAttrs
)`
  ${withIconStyles}
`

const ActionMenuItem = styled(DropdownMenu.Item)`
  ${withIconStyles}
`

const ActionButtonContainer = styled(Flex)<{ slim?: boolean }>`
  ${({ slim }) =>
    slim &&
    `
      & > *:first-child button {
        border-right: 0;
        border-radius: 4px 0 0 4px;
      }
      & > *:last-child {
        border-radius: 0 4px 4px 0;
      }
    `}
`

type WorkerActionsProps = {
  worker: ComponentProps<typeof BlockModal>['worker']
  slim?: boolean
}

enum ModalType {
  SendTip = 'send-tip',
  ScheduleShift = 'schedule-shift',
  AddToList = 'add-to-list',
  GiveBonus = 'give-bonus',
  BlockWorker = 'block-worker',
  None = '',
}

export function WorkerActions({ worker, slim }: WorkerActionsProps) {
  const [selection, setSelection] = useState<ModalType>(ModalType.None)
  const [isAlertOpen, setIsAlertOpen] = useState(false)

  const { mutate: updateWorkerStatusForCompany } =
    useUpdateWorkerStatusForCompanyMutation(worker.id)

  const isFavorite = worker.statusForCompany === StatusForCompanyEnum.Favorite
  const isBlocked = worker.statusForCompany === StatusForCompanyEnum.Blocked
  const positionTemplateBlockCount = worker.positionTemplateBlocks.filter(
    (block) => block.blocked
  ).length

  const isTippingEnabled = useIsTippingEnabled()
  const { data: tipHistory } = useTipHistory(
    { workerId: worker.id },
    { enabled: isTippingEnabled }
  )
  const canSendTip = Boolean(tipHistory?.workHistory?.length)

  const handleFavorite = () => {
    updateWorkerStatusForCompany({
      action: isFavorite ? 'unfavorite' : 'favorite',
    })
  }

  const handleCompanyUnblock = () => {
    updateWorkerStatusForCompany({
      action: 'unblock',
    })
  }

  const handleClose = () => {
    setSelection(ModalType.None)
  }

  return (
    <>
      {slim ? null : (
        <BlockedAlert
          isCompanyBlocked={isBlocked}
          isOpen={isAlertOpen}
          onClick={() => setIsAlertOpen(!isAlertOpen)}
          positionTemplateBlockCount={positionTemplateBlockCount}
          workerName={worker.name}
        />
      )}

      <ActionButtonContainer gap={slim ? 0 : 2} slim={slim}>
        {isBlocked ? (
          <Tooltip content="Click to Unblock">
            <ActionButton slim={slim} onClick={handleCompanyUnblock} danger>
              <XIcon size={16} /> {slim ? '' : 'Blocked'}
            </ActionButton>
          </Tooltip>
        ) : (
          <Tooltip
            content={isFavorite ? 'Click to Unfavorite' : 'Click to Favorite'}
          >
            <ActionButton slim={slim} onClick={handleFavorite}>
              <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
            </ActionButton>
          </Tooltip>
        )}

        <DropdownMenu
          trigger={
            <Trigger slim={slim}>
              {slim ? '' : 'More Actions'} <ChevronDownIcon size={18} />
            </Trigger>
          }
        >
          <ActionMenuItem
            key={ModalType.ScheduleShift}
            onSelect={() => setSelection(ModalType.ScheduleShift)}
          >
            <ClockIcon size={16} /> Schedule Shift
          </ActionMenuItem>

          <ActionMenuItem
            key={ModalType.AddToList}
            onSelect={() => setSelection(ModalType.AddToList)}
          >
            <PlusIcon size={16} /> Add to List
          </ActionMenuItem>

          <ActionMenuItem
            key={ModalType.GiveBonus}
            onSelect={() => setSelection(ModalType.GiveBonus)}
          >
            <GiftIcon size={16} /> Give Bonus
          </ActionMenuItem>

          {canSendTip && (
            <ActionMenuItem
              key={ModalType.SendTip}
              onSelect={() => setSelection(ModalType.SendTip)}
            >
              <Mail size={16} /> Send Tip
            </ActionMenuItem>
          )}

          {!isBlocked ? (
            <ActionMenuItem
              key={ModalType.BlockWorker}
              onSelect={() => setSelection(ModalType.BlockWorker)}
            >
              <XIcon size={16} /> Block / Unblock Worker
            </ActionMenuItem>
          ) : null}
        </DropdownMenu>
      </ActionButtonContainer>

      <ScheduleShiftModal
        worker={worker}
        isOpen={selection === ModalType.ScheduleShift}
        onClose={handleClose}
      />

      <AddWorkerToListModal
        worker={worker}
        isOpen={selection === ModalType.AddToList}
        onClose={handleClose}
      />

      <GiveBonusModal
        workerId={worker.id}
        workerName={worker.name}
        open={selection === ModalType.GiveBonus}
        onClose={handleClose}
      />

      <SendTipModal
        workerId={worker.id}
        workerName={worker.name}
        isOpen={selection === ModalType.SendTip}
        onClose={handleClose}
      />

      <BlockModal
        key={`block-modal-open--${selection === ModalType.BlockWorker}`} // Reset initial step
        isOpen={selection === ModalType.BlockWorker}
        worker={worker}
        onClose={handleClose}
      />
    </>
  )
}
